import React from 'react';
import { Link } from 'react-router-dom';
import LegalPageLayout from './LegalPageLayout';

const UserDataDeletion = () => {
    return (
        <LegalPageLayout>
            <div className="legal-page">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-lg-10">
                            {/* Breadcrumb */}
                            <nav aria-label="breadcrumb" className="mt-4">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        <Link to="/">Home</Link>
                                    </li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        User Data Deletion
                                    </li>
                                </ol>
                            </nav>

                            {/* Page Header */}
                            <div className="text-center mb-5">
                                <h1 className="h2 font-weight-bold">User Data Deletion Policy</h1>
                                <p className="lead text-muted">Last updated: {new Date().toLocaleDateString()}</p>
                            </div>

                            {/* Content */}
                            <div className="legal-content">
                                <section className="mb-5">
                                    <h2>1. Introduction</h2>
                                    <p>
                                        At SalesPinch, we respect your right to control your personal data. This policy explains how you can request the deletion of your personal information from our systems and what data will be removed when you make such a request.
                                    </p>
                                    <p>
                                        We are committed to complying with data protection regulations, including the General Data Protection Regulation (GDPR) and other applicable privacy laws that grant you the "right to be forgotten."
                                    </p>
                                </section>

                                <section className="mb-5">
                                    <h2>2. Your Right to Data Deletion</h2>
                                    <p>You have the right to request the deletion of your personal data in the following circumstances:</p>
                                    <ul>
                                        <li>Your personal data is no longer necessary for the purposes for which it was collected</li>
                                        <li>You withdraw your consent for data processing</li>
                                        <li>You object to the processing and there are no overriding legitimate grounds</li>
                                        <li>Your personal data has been unlawfully processed</li>
                                        <li>Your personal data must be erased to comply with a legal obligation</li>
                                        <li>Your personal data was collected in relation to the offer of information society services</li>
                                    </ul>
                                </section>

                                <section className="mb-5">
                                    <h2>3. Data That Will Be Deleted</h2>
                                    
                                    <h3>3.1 Account Information</h3>
                                    <p>When you request account deletion, we will remove:</p>
                                    <ul>
                                        <li>Your account profile and personal details</li>
                                        <li>Contact information (name, email, phone number)</li>
                                        <li>Company information and job title</li>
                                        <li>Account credentials and login information</li>
                                        <li>Profile preferences and settings</li>
                                    </ul>

                                    <h3>3.2 Lead Management Data</h3>
                                    <p>We will also delete:</p>
                                    <ul>
                                        <li>All leads and contact information you've stored</li>
                                        <li>Interaction history and communication logs</li>
                                        <li>Sales pipeline data and progress tracking</li>
                                        <li>Notes, comments, and attachments related to leads</li>
                                        <li>Custom fields and data you've created</li>
                                    </ul>

                                    <h3>3.3 Usage and Analytics Data</h3>
                                    <p>The following data will be removed:</p>
                                    <ul>
                                        <li>Platform usage statistics and analytics</li>
                                        <li>Feature usage patterns and preferences</li>
                                        <li>Session data and login history</li>
                                        <li>Device information and IP addresses</li>
                                        <li>Cookies and tracking data associated with your account</li>
                                    </ul>

                                    <h3>3.4 Payment and Billing Information</h3>
                                    <p>We will delete:</p>
                                    <ul>
                                        <li>Payment method details and billing information</li>
                                        <li>Subscription history and plan details</li>
                                        <li>Invoice and transaction records</li>
                                        <li>Billing address and tax information</li>
                                    </ul>
                                </section>

                                <section className="mb-5">
                                    <h2>4. Data That May Be Retained</h2>
                                    <p>In certain circumstances, we may be required to retain some data:</p>
                                    
                                    <h3>4.1 Legal Obligations</h3>
                                    <ul>
                                        <li>Data required for tax and accounting purposes</li>
                                        <li>Information needed to comply with regulatory requirements</li>
                                        <li>Records required for legal proceedings or investigations</li>
                                        <li>Data necessary to protect our legal rights</li>
                                    </ul>

                                    <h3>4.2 Service Continuity</h3>
                                    <ul>
                                        <li>Backup data for a limited period (typically 30 days)</li>
                                        <li>System logs for security and fraud prevention</li>
                                        <li>Aggregated, anonymized data for analytics</li>
                                    </ul>

                                    <h3>4.3 Third-Party Integrations</h3>
                                    <p>Please note that data shared with third-party services (such as payment processors, email services, or analytics providers) may be subject to their own retention policies. You may need to contact these services directly to request data deletion.</p>
                                </section>

                                <section className="mb-5">
                                    <h2>5. How to Request Data Deletion</h2>
                                    
                                    <h3>5.1 Through Your Account</h3>
                                    <p>The easiest way to request data deletion is through your SalesPinch account:</p>
                                    <ol>
                                        <li>Log in to your SalesPinch account</li>
                                        <li>Go to Account Settings</li>
                                        <li>Navigate to the "Privacy & Data" section</li>
                                        <li>Click on "Request Data Deletion"</li>
                                        <li>Confirm your request and provide any required information</li>
                                    </ol>

                                    <h3>5.2 Via Email</h3>
                                    <p>You can also request data deletion by emailing us at:</p>
                                    <div className="alert alert-info">
                                        <strong>Email:</strong> app@salespinch.com<br />
                                        <strong>Subject:</strong> Data Deletion Request
                                    </div>
                                    <p>Please include the following information in your email:</p>
                                    <ul>
                                        <li>Your full name and email address associated with your account</li>
                                        <li>Your company name (if applicable)</li>
                                        <li>Reason for the deletion request (optional)</li>
                                        <li>Confirmation that you understand the consequences of account deletion</li>
                                    </ul>

                                    <h3>5.3 Via Contact Form</h3>
                                    <p>You can also use our contact form at <Link to="/contact-page">Contact Us</Link> page and select "Data Deletion Request" as the subject.</p>
                                </section>

                                <section className="mb-5">
                                    <h2>6. Processing Timeline</h2>
                                    <p>We will process your data deletion request as follows:</p>
                                    <ul>
                                        <li><strong>Immediate Response:</strong> We will acknowledge your request within 24 hours</li>
                                        <li><strong>Verification:</strong> We may need to verify your identity to protect your data</li>
                                        <li><strong>Processing:</strong> Data deletion will be completed within 30 days of verification</li>
                                        <li><strong>Confirmation:</strong> You will receive confirmation once the deletion is complete</li>
                                    </ul>
                                    <p><strong>Note:</strong> In complex cases or when dealing with large amounts of data, the process may take up to 45 days. We will keep you informed of any delays.</p>
                                </section>

                                <section className="mb-5">
                                    <h2>7. Consequences of Data Deletion</h2>
                                    <p>Please be aware that requesting data deletion will result in:</p>
                                    <ul>
                                        <li><strong>Permanent Loss:</strong> All your data will be permanently deleted and cannot be recovered</li>
                                        <li><strong>Account Termination:</strong> Your SalesPinch account will be permanently closed</li>
                                        <li><strong>Service Access:</strong> You will lose access to all SalesPinch services and features</li>
                                        <li><strong>Data Recovery:</strong> Deleted data cannot be restored or recovered</li>
                                        <li><strong>Third-Party Data:</strong> You may need to separately request deletion from integrated services</li>
                                    </ul>
                                    <div className="alert alert-warning">
                                        <strong>Important:</strong> Before requesting data deletion, please ensure you have backed up any important information you may need in the future.
                                    </div>
                                </section>

                                <section className="mb-5">
                                    <h2>8. Exceptions and Limitations</h2>
                                    <p>We may not be able to delete your data in the following circumstances:</p>
                                    <ul>
                                        <li>When retention is required by law or regulation</li>
                                        <li>When data is needed for ongoing legal proceedings</li>
                                        <li>When deletion would compromise the integrity of our systems</li>
                                        <li>When data has been anonymized and is no longer personally identifiable</li>
                                        <li>When deletion would prevent us from providing services to other users</li>
                                    </ul>
                                    <p>If we cannot fulfill your deletion request, we will explain the reasons and inform you of your right to lodge a complaint with the relevant data protection authority.</p>
                                </section>

                                <section className="mb-5">
                                    <h2>9. Contact Information</h2>
                                    <p>If you have any questions about this data deletion policy or need assistance with your request, please contact us:</p>
                                    <div className="row">
                                         <div className="col-md-6">
                                            <h4>General Privacy Inquiries</h4>
                                            <p>
                                                Email: app@salespinch.com<br />
                                                Contact Form: <Link to="/contact-page">Contact Us</Link>
                                            </p>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-5">
                                    <h2>10. Updates to This Policy</h2>
                                    <p>
                                        We may update this User Data Deletion Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated policy on our website and updating the "Last updated" date.
                                    </p>
                                    <p>
                                        We encourage you to review this policy periodically to stay informed about your data deletion rights and our practices.
                                    </p>
                                </section>

                                <div className="text-center mt-5">
                                    <Link to="/data-deletion" className="btn btn-primary btn-lg">
                                        Request Data Deletion
                                    </Link>
                                </div>

                                {/* Footer Links */}
                                <div className="text-center mt-5 pt-4 border-top">
                                    <div className="row">
                                        <div className="col-md-3">
                                            <Link to="/privacy" className="text-decoration-none">
                                                Privacy Policy
                                            </Link>
                                        </div>
                                        <div className="col-md-3">
                                            <Link to="/terms" className="text-decoration-none">
                                                Terms of Service
                                            </Link>
                                        </div>
                                        <div className="col-md-3">
                                            <Link to="/disclaimer" className="text-decoration-none">
                                                Disclaimer
                                            </Link>
                                        </div>
                                        <div className="col-md-3">
                                            <Link to="/contact-page" className="text-decoration-none">
                                                Contact Us
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </LegalPageLayout>
    );
};

export default UserDataDeletion; 